<script setup>
// [factorem-grace-ask] Full-height "Ask Grace" page. The Grace base URL comes
// from the dashboard app titled "Grace" (content url e.g.
// http://localhost:8030/panel -> http://localhost:8030/ask). ?q= is passed
// through. The iframe may post {event:'factorem:openConversation',
// data:{conversationId, messageId}} to open a conversation in this tab.
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const GRACE_APP_TITLES = ['grace', 'grace ai'];
const ASK_FRAME_CLASS = 'factorem-grace-ask';
const OPEN_CONVERSATION_EVENT = 'factorem:openConversation';

const store = useStore();
const route = useRoute();
const router = useRouter();
const frameRef = ref(null);

const dashboardApps = computed(() => store.getters['dashboardApps/getRecords']);
const uiFlags = computed(() => store.getters['dashboardApps/getUIFlags']);

// Grace's panel URL from the dashboard app; Ask lives next to it (…/panel -> …/ask), keeping any path prefix
const graceUrl = computed(() => {
  const app = dashboardApps.value.find(
    a => GRACE_APP_TITLES.includes((a.title || '').trim().toLowerCase())
  );
  try {
    return new URL(app?.content?.[0]?.url);
  } catch {
    return null;
  }
});

const askUrl = computed(() => {
  if (!graceUrl.value) return '';
  const url = new URL('ask', graceUrl.value);
  url.search = graceUrl.value.search; // keep the panel URL's ?key= (Grace refuses its API without it)
  const q = typeof route.query.q === 'string' ? route.query.q : '';
  if (q) url.searchParams.set('q', q);
  return url.toString();
});

const parseFrameMessage = raw => {
  if (raw && typeof raw === 'object') return raw;
  if (typeof raw !== 'string' || !raw.startsWith('{')) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const onFrameMessage = event => {
  const frameWindow = frameRef.value?.contentWindow;
  if (
    !frameWindow ||
    event.source !== frameWindow ||
    event.origin !== graceUrl.value?.origin
  )
    return;
  const message = parseFrameMessage(event.data);
  if (message?.event !== OPEN_CONVERSATION_EVENT) return;
  const conversationId = Number(message.data?.conversationId);
  if (!Number.isInteger(conversationId) || conversationId <= 0) return;
  const messageId = Number(message.data?.messageId);
  router.push({
    name: 'inbox_conversation',
    params: {
      accountId: route.params.accountId,
      conversation_id: conversationId,
    },
    query:
      Number.isInteger(messageId) && messageId > 0 ? { messageId } : {},
  });
};

onMounted(() => {
  window.addEventListener('message', onFrameMessage);
  if (!dashboardApps.value.length) store.dispatch('dashboardApps/get');
});
onUnmounted(() => window.removeEventListener('message', onFrameMessage));
</script>

<template>
  <section class="flex flex-col w-full h-full min-h-0 bg-n-surface-1">
    <iframe
      v-if="askUrl"
      ref="frameRef"
      :key="askUrl"
      :class="ASK_FRAME_CLASS"
      class="flex-1 w-full h-full min-h-0 border-0"
      :src="askUrl"
      title="Ask Grace AI"
      allow="clipboard-write"
    />
    <div
      v-else-if="uiFlags.isFetching"
      class="flex items-center justify-center w-full h-full"
    >
      <Spinner :size="24" />
    </div>
    <div
      v-else
      class="flex flex-col items-center justify-center w-full h-full gap-2 px-6 text-center"
    >
      <span class="i-lucide-sparkles size-6 text-n-slate-10" />
      <p class="text-base font-medium text-n-slate-12">
        Grace isn't connected
      </p>
      <p class="text-sm text-n-slate-11 max-w-md">
        Add a dashboard app titled "Grace" (Settings, Integrations, Dashboard
        apps) pointing at the Grace panel URL, then reload this page.
      </p>
    </div>
  </section>
</template>
